import React from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemButton
} from "react-accessible-accordion";

import {
  NamedLink,
} from '../../components';

const FAQPageAccordionForTeachers = () => {

  return (
    <Accordion preExpanded={[1]} key="becomeAHostPageFAQ" allowZeroExpanded >
      <AccordionItem uuid={1}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What does AllCourse offer teachers?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            On AllCourse, teachers create a profile that can be discovered by administrators who want to offer you work.
            It’s often hard for school admins to find qualified teachers in their area (especially now due to the ongoing teacher shortage).
            If admins are willing to have the teacher be online, they can hire you on AllCourse.
          </p>
          <p>
            You can also list courses you’re committing to teaching, and set your rate of compensation.
            Students from around your state or around the U.S. can join your course, after their schools (or their parents) purchase a seat.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={2}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Can anyone teach through AllCourse?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            No. To teach on AllCourse, teachers MUST be qualified to teach semester-long, for-credit courses in schools.
            Public school, private, or charter school teachers are all welcome to teach. Teachers who don’t have a public
            school teaching license (e.g. private or charter school teachers) must demonstrate their qualifications in their
            teacher profile, and will only appear in search results for private or charter schools. Public school teachers
            must list their license number in their profile, and it must be current and in good standing. AllCourse does background
            checks on teachers and will suspend accounts that don’t meet AllCourse standards.
          </p>
          <p>
            AllCourse is a place for schools, students, and parents to find exceptional online teachers offering a variety of
            subject matters that can be accepted for credit in traditional schools. Only qualified and credentialed teachers with
            prior teaching experience can list their services on AllCourse.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={3}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What if I’m licensed in multiple states?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Great! Then you can list courses for all of those states and increase your income opportunities.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={4}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How easy or hard is it to get licensed in additional states?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Many states have reciprocity with each other that makes it significantly easier to teach in those states.
            What we hear from teachers is that reciprocity can often make it quick and easy to become licensed in a new
            state. Our <NamedLink name="TeacherHandbookPage" >Teacher Handbook</NamedLink> contains information on state-by-state reciprocity agreements.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={5}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How do I earn income using AllCourse?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            List courses that you will teach. Courses will be discoverable by any school admins
            and parents who may need a course like yours for their students.
          </p>
          <p>
            Once you’ve listed at least one course, school admins and parents can begin to contact you about
            buying seats in your course—or perhaps buying all the seats in the course.
          </p>
          <p>
            If you don’t want to commit to a schedule just yet, you can leave the schedule blank. School admins
            and parents will still be able to contact you even if you haven’t listed a schedule.
          </p>
          <p>
            You can tell your friends and colleagues know about your courses, and market your courses on social
            media. Do you teach a course particularly well? Do you teach a subject that’s in-demand or hard to
            find? Then list a course and let people know about it.
          </p>
          <p>
            When you’re ready to commit to teaching a particular course on a particular schedule, set the per-seat
            rate you’d like to make, and begin to let people know about your course. School admins and parents can
            begin to purchase seats in your course for their students.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={6}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How much can I earn?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            That depends on you. While other online teaching opportunities often pay just $7-15/hour, on AllCourse
            you should expect to make something close to your full hourly salary or more. You’re teaching the same courses
            you’ve been developing throughout your career. There’s no reason you should make less just because you’re teaching them online.
          </p>
          <p>
            As you’d expect, the more courses you teach, and the more students you teach, the more you will make.
            If your courses prove to be popular, you will be able to increase your per-seat rate, or increase your
            class size, either of which will increase your compensation. (Of course, you may want to limit class sizes
            in order to attract more buyers or to offer your students more personal attention.)
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={7}>
        <AccordionItemHeading>
          <AccordionItemButton>
            When are teachers paid?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Teachers are paid twice: 40% up front (as soon as the free-cancellation period ends)
            and 60% at the end after you’ve delivered the full course and submitted grades (if any).
            If that won’t work for you, contact <a href="mailto:support@allcourse.com">support@allcourse.com</a> to see if we can work out a
            different arrangement that meets your needs.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={8}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Why does AllCourse use a per-seat payment model rather than an hourly salary?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Some purchasers will only want one seat in a given course. Some will want several. Some will want the entire section.
            Purchases won’t want to pay a teacher’s full hourly salary if they’re only buying one seat, especially since there will be other
            purchasers buying seats in that same course. The only way to make payment fair for purchasers is to use a per-seat model.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={9}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How can I set my per-seat rate so that it approximates my hourly salary?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Teachers can set a per-seat rate based on how many students they think will enroll and how many hours they expect to work.
          </p>
          <p>
            Example: Let’s say you want to make the equivalent of $32/hour. If you expect the class size to be 16,
            and you expect to do 200 hours of total work (teaching, collecting homework, grading, etc.),
            then you would set the per-seat fee to $400.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={10}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What is AllCourse’s revenue share?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            While many marketplaces, even in education, charge a 20-30% service fee to pay for all the costs
            of running the service, AllCourse is different. AllCourse’s service fee is normally 15% — and has been
            reduced to just 12.5% for any course taught in the 2022-23 school year.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={11}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Are AllCourse teachers eligible for medical benefits?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            No. Teachers don’t work for AllCourse. Teachers on AllCourse are independent contractors.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={12}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What courses can I teach?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Whatever your heart desires. That said, school administrators are coming to AllCourse to find
            teachers who are credentialed to teach for-credit courses in their state. If the course needs to
            be standards-aligned, admins will be specifically looking for that too. While you can offer any course you like,
            you are most likely to attract buyers if your credentials are clearly listed on your profile and
            the course is generally acceptable for credit.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={13}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What times should I schedule classes?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            In general, schools prefer to buy courses (even online courses) that are delivered during their school
            day — especially in core curricula. However, it’s also true that many schools do not mind if their students take
            online courses that are delivered before or after the school day, or even on evenings and weekends. Our advice is
            to experiment, and be flexible. Listen to the schools and adjust to their needs. After all, they are the customers.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={14}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Are courses taught live or asynchronously?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Courses are generally taught live. However, video recordings are something you should create as a matter
            of course (forgive the pun) as a resource for students who miss live sessions. You choose which video
            platform you’re most comfortable with.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={15}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Do schools have on-ground teachers facilitating these courses? Or am I the only teacher involved in the courses I teach?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            It depends. Some schools may have a local teacher in school facilitating a course that you are teaching.
            They may do this for a variety of reasons. Perhaps they don’t have a teacher who is qualified to teach your particular
            subject area, but they do have a teacher on the ground who can help guide the students through your material. Or,
            they may wish to have a proctor available to step into the classroom whenever needed to monitor student behavior.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={16}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What’s to stop admins and teachers from meeting on AllCourse and then transacting offline?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            It is a violation of our Terms of Service. AllCourse’s service fees are modest compared to other marketplaces,
            and they are necessary in order to keep the platform running and to add new features and improvements. Schools
            and teachers who meet on AllCourse and then transact offline in order to avoid paying service fees will, at a minimum,
            be banned from using AllCourse in the future.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={17}>
        <AccordionItemHeading>
          <AccordionItemButton>
            Does AllCourse allow administrators and students to rate or review my classes?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Yes. We recognize that this is a little different from how things work in a school building, and it may be uncomfortable
            for some teachers. But this is a different experience for schools as well, and it requires some new approaches.
            Administrators buying seats on AllCourse don’t get to know teachers the way they would by working together in the same
            school building. So admins need other mechanisms that allow them to trust what they’re getting. Those include clearly
            listed credentials on your teacher profile page, references from administrators you’ve worked with in the past, and sample
            videos that showcase your personal style. It also includes teacher ratings and reviews.
          </p>
          <p>
            AllCourse asks all school administrators who’ve purchased seats in your courses, as well as a random sampling of students,
            to rate the experience and offer reviews. Over time, you will find that one of the best ways to build your presence on
            AllCourse is to make sure your ratings and reviews are healthy. The more you have, the better — when it comes to ratings,
            buyers trust quantity. And, obviously, try to ensure that they’re positive. Buyers will often search for teachers or
            courses on the basis of ratings.
          </p>
        </AccordionItemPanel>
      </AccordionItem>


      <AccordionItem uuid={18}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How can I stand out on AllCourse?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            To make your profile and course listings stand out, we highly recommend that you list all credentials,
            qualifications, and awards. If you want to pursue public school opportunities, you will need to list your
            teacher license and keep it in good standing. We also recommend you provide references from school administrators
            you’ve worked with, and sample videos of your teaching style. One of the best ways to build your presence on
            AllCourse is to keep your ratings and reviews as positive as you can. Take a look at our
            <NamedLink name="TeacherHandbookPage" >Teacher Handbook</NamedLink> to read tips from other teachers about running effective online classes.
          </p>
        </AccordionItemPanel>
      </AccordionItem>


      <AccordionItem uuid={19}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How can I get myself more prepared to teach online courses for AllCourse?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            Check our AllCourse’s <NamedLink name="TeacherHandbookPage">Teacher Handbook</NamedLink>. It contains a wealth of resources to help create content, set ready to
            teach online, and teach wonderful online classes.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={20}>
        <AccordionItemHeading>
          <AccordionItemButton>
            How does the referral bonus work and what are the eligibility requirements?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            For a limited time, earn cash for every teacher and school admin you invite to join the AllCourse community.
          </p>
          <p>
            For every teacher you refer, we’ll give you the first $200 in service fees they generate for AllCourse. For every admin you refer,
            we’ll give you the first $400 in service fees they generate for AllCourse
          </p>
          <p>
            Examples: You refer a teacher who teaches a course that generates $400 in service fees for AllCourse. You receive the first $200.
            Or, you refer a school administrator who ends up purchasing a number of seats in various courses for their students, generating
            $500 in service fees for AllCourse. You receive the first $400.
          </p>
          <p>
            For a referral to be valid, it must be the first time that person joined AllCourse. Referral bonuses are deducted from
            AllCourse service fees, which means you will receive bonuses when teachers you refer have taught courses on AllCourse
            and/or when admins you refer have purchased courses on AllCourse.
          </p>
          <p>
            These referral bonuses are meant to be a big THANK YOU to early adopters for helping spread the word.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

      <AccordionItem uuid={21}>
        <AccordionItemHeading>
          <AccordionItemButton>
            What is your refund policy?
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel>
          <p>
            School admins can cancel seats and receive a refund, depending on when they cancel. For most courses, you can
            cancel and request a refund anytime during the first week of classes. This allows everyone involved to make sure
            that the fit is right between the student and the class. However, some courses are very short, such as custom
            courses that teachers offer to a particular school to solve a short-term staffing crisis. If a course is less
            than 3 weeks long, you can cancel and request a refund anytime prior to the first class session.
          </p>
          <p>
            In the event that a teacher wishes to cancel a course listing, they may do so under the following conditions:
          </p>
          <ul>
            <li>the minimum number of seats have not yet been met; and</li>
            <li>the course start date has not passed</li>
          </ul>
          <p>
            This allows for teachers to cancel courses that do not meet their profitability threshold, but protects school
            administrators so that they can rely on seats they pay for being fulfilled. If a teacher cancels a course,
            100% of the cost of the seats will be refunded to all purchasers.
          </p>
        </AccordionItemPanel>
      </AccordionItem>

    </Accordion>
  );
};

export default FAQPageAccordionForTeachers;
